import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  Link,
  Tabs,
  Tab,
  Button
} from '@mui/material';
import { API_BASE_URL, DEFAULT_PAGINATION_LIMIT } from '../config'; // Import centralized constants

// Match types from App.tsx or a shared types file
interface AppUser {
  apiKey?: string;
  // other user fields...
}

type UsageGranularity = 'hour' | 'day' | 'month';

interface UsageRecord {
  _id: string; 
  hourStartTimestamp?: string; // Specific to hourly
  dayStartTimestamp?: string;  // Specific to daily
  monthStartTimestamp?: string; // Specific to monthly
  modelVersion: string;
  totalPromptTokenCount: number;
  totalCandidatesTokenCount: number;
  totalTotalTokenCount: number;
  apiCallCount: number;
  totalCostUSD: number;
  lastUpdatedAt: string;
}

interface PaginatedUsageResponse {
  data: UsageRecord[];
  page: number;
  limit: number;
  totalRecords: number;
  totalPages: number;
}

interface UsageDisplayProps {
  currentUser: AppUser | null;
  showSnackbar: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
}

// const USAGE_API_BASE_URL = 'https://myhome.cfc.io/api/unitai'; // Removed
// const DEFAULT_LIMIT = 10; // Removed, now imported as DEFAULT_PAGINATION_LIMIT

const UsageDisplay: React.FC<UsageDisplayProps> = ({ currentUser, showSnackbar }) => {
  const [usageData, setUsageData] = useState<UsageRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<UsageGranularity>('hour');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const fetchUsageData = useCallback(async (page: number, granularity: UsageGranularity, loadMore = false) => {
    if (!currentUser || !currentUser.apiKey) {
      setUsageData([]);
      return;
    }

    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setUsageData([]); // Clear previous data when not loading more (e.g. tab change)
    }
    setError(null);

    try {
      const response = await axios.get<PaginatedUsageResponse>(
        `${API_BASE_URL}/${currentUser.apiKey}/usage/${granularity}?page=${page}&limit=${DEFAULT_PAGINATION_LIMIT}`
      );
      setUsageData(prevData => loadMore ? [...prevData, ...response.data.data] : response.data.data);
      setCurrentPage(response.data.page);
      setTotalPages(response.data.totalPages);
      setTotalRecords(response.data.totalRecords);

      if (response.data.data.length === 0 && !loadMore) {
        // showSnackbar(`No ${granularity}ly usage data found.`, 'info');
      }
    } catch (err: any) {
      console.error(`Failed to fetch ${granularity}ly usage data:`, err);
      let errMsg = `Could not load ${granularity}ly usage data.`;
      if (axios.isAxiosError(err) && err.response) {
        if (err.response.status === 404) {
          errMsg = `No ${granularity}ly usage data found.`;
          if (!loadMore) setUsageData([]); // Clear data on 404 if it's a fresh load
        } else {
          errMsg = err.response.data?.message || err.response.data?.error || errMsg;
        }
      }
      setError(errMsg);
    }
    setLoading(false);
    setLoadingMore(false);
  }, [currentUser]); // Removed showSnackbar from deps as it might cause re-fetches if not stable

  useEffect(() => {
    setCurrentPage(1); // Reset page when tab changes
    setTotalPages(1);
    setTotalRecords(0);
    fetchUsageData(1, selectedTab);
  }, [selectedTab, currentUser?.apiKey]); // Re-fetch if tab or apiKey changes

  const handleTabChange = (event: React.SyntheticEvent, newValue: UsageGranularity) => {
    setSelectedTab(newValue);
  };

  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      fetchUsageData(currentPage + 1, selectedTab, true);
    }
  };

  const getTimestampForRecord = (record: UsageRecord): string => {
    return record.hourStartTimestamp || record.dayStartTimestamp || record.monthStartTimestamp || 'N/A';
  };
  
  const getPrimaryTextForRecord = (record: UsageRecord, granularity: UsageGranularity): string => {
    const date = new Date(getTimestampForRecord(record));
    let options: Intl.DateTimeFormatOptions = {};
    if (granularity === 'hour') options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    else if (granularity === 'day') options = { year: 'numeric', month: 'long', day: 'numeric' };
    else if (granularity === 'month') options = { year: 'numeric', month: 'long' };
    return `${granularity.charAt(0).toUpperCase() + granularity.slice(1)}: ${date.toLocaleString(undefined, options)}`;
};


  if (!currentUser || !currentUser.apiKey) {
    return (
        <Paper elevation={1} sx={{ padding: 2, marginTop: 2, textAlign: 'left' }}>
            <Typography variant="h6" gutterBottom>API Usage Details</Typography>
            <Typography variant="body2" color="text.secondary">
                API key not available. Usage data cannot be displayed.
            </Typography>
        </Paper>
    );
  }
  
  return (
    <Paper elevation={1} sx={{ padding: 2, marginTop: 2, textAlign: 'left' }}>
      <Typography variant="h6" gutterBottom>API Usage Details</Typography>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={selectedTab} onChange={handleTabChange} aria-label="Usage granularity tabs">
          <Tab label="Hourly" value="hour" />
          <Tab label="Daily" value="day" />
          <Tab label="Monthly" value="month" />
        </Tabs>
      </Box>

      {loading && !loadingMore && <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
      {!loading && !error && usageData.length === 0 && (
        <Typography sx={{ color: 'grey.600', fontStyle: 'italic', my: 2, textAlign: 'center' }}>
          No {selectedTab}ly usage data recorded yet.
        </Typography>
      )}

      {!error && usageData.length > 0 && (
        <List dense>
          {usageData.map((record, index) => (
            <React.Fragment key={record._id || `${selectedTab}-${index}`}>
              <ListItem sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1.5 }}>
                <ListItemText 
                  primary={getPrimaryTextForRecord(record, selectedTab)}
                  secondary={`Model: ${record.modelVersion}`}
                  sx={{ mb: 0.5}}
                />
                <Typography variant="body2" component="div" sx={{ pl: 2, width: '100%', lineHeight: 1.6 }}>
                    Tokens (Prompt/Completion/Total): {record.totalPromptTokenCount} / {record.totalCandidatesTokenCount} / {record.totalTotalTokenCount}
                    <br />
                    API Calls: {record.apiCallCount}
                    <br />
                    Cost: ${record.totalCostUSD.toFixed(6)}
                    <br />
                    <span style={{fontSize: '0.75rem', color: 'grey'}}>
                        Last Updated: {new Date(record.lastUpdatedAt).toLocaleTimeString()}
                    </span>
                </Typography>
              </ListItem>
              {index < usageData.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}

      {loadingMore && <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress size={30} /></Box>}
      
      {!loading && !error && usageData.length > 0 && currentPage < totalPages && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="outlined" onClick={handleLoadMore} disabled={loadingMore}>
            Load More ({currentPage}/{totalPages})
          </Button>
        </Box>
      )}
       <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
        Displaying {usageData.length} of {totalRecords} records. All timestamps are based on server time.
        For questions about your usage, please see the <Link href="#" onClick={(e) => { e.preventDefault(); showSnackbar('Link to detailed billing/FAQ page clicked!','info')}}>FAQ</Link> or contact support.
      </Typography>
    </Paper>
  );
};

export default UsageDisplay; 